/**
 * The DPDP consent record: what the user ticked on the way in, and getting it
 * into the backend once there is a session to attach it to.
 *
 * The two halves are here together because they are two ends of one gap. The
 * consent screen collects the ticks *before* any account exists - that is the
 * point of it sitting where it does - so nothing can be recorded at the moment
 * it is given. What the user agreed to is parked in session storage, and the
 * bootstrap handshake posts it as soon as the session is live.
 *
 * Ordering matters and is not incidental: the documents are read first, because
 * that read is what supplies the version each acceptance must be recorded
 * against. Hard-coding "1.0" here would quietly mis-record every user the day a
 * document is republished.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../types/auth.types";
import type {
  ConsentAcceptance,
  ConsentAcceptanceResponse,
  ConsentDocument,
  ConsentDocumentResponse,
  ConsentDocumentType,
  ConsentDocumentsResponse,
  RecordConsentPayload,
  RecordConsentResponse,
} from "../types/consent.types";

// ---------------------------------------------------------------------------
// What the user agreed to, held across the gap
// ---------------------------------------------------------------------------

/**
 * Session storage, not local: consent given on a sign-up that was abandoned
 * half way should not be waiting for the next person to use the browser.
 *
 * It has to survive a full page load either way. The Google route leaves for
 * Google and comes back as a fresh document, and Clerk can finish even the
 * email route with a top-level redirect, so nothing held in memory would still
 * be there by the time the session goes active.
 */
const PENDING_CONSENT_KEY = "naurist.pendingConsent";

/**
 * Records which documents the user has just agreed to, for the bootstrap
 * handshake to post once the session exists.
 *
 * Storage can throw outright - Safari private mode, third-party cookie blocking
 * in an embedded context. Consent that cannot be parked is consent that cannot
 * be recorded, so the caller is told rather than it failing silently.
 */
export function rememberGivenConsent(types: readonly ConsentDocumentType[]): boolean {
  try {
    sessionStorage.setItem(PENDING_CONSENT_KEY, JSON.stringify(types));
    return true;
  } catch {
    return false;
  }
}

/**
 * The consent waiting to be recorded, or null if there is none - which is the
 * normal case for a log-in, and is what keeps this whole step to the sign-up
 * flow without anything having to pass a flag down to it.
 *
 * Reading does not clear: a POST that fails must leave the record in place, or
 * the agreement is lost with no way to get it back short of asking again.
 * `clearGivenConsent` is called once the backend has it.
 */
export function readGivenConsent(): ConsentDocumentType[] | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_CONSENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    // Anything that is not the array we wrote is treated as absent rather than
    // trusted - it is user-writable storage, and an acceptance is not something
    // to assert on the strength of a malformed value.
    return Array.isArray(parsed) && parsed.every((t) => typeof t === "string")
      ? (parsed as ConsentDocumentType[])
      : null;
  } catch {
    return null;
  }
}

export function clearGivenConsent(): void {
  try {
    sessionStorage.removeItem(PENDING_CONSENT_KEY);
  } catch {
    /* nothing to clean up if it could not be written in the first place */
  }
}

// ---------------------------------------------------------------------------
// The endpoints
// ---------------------------------------------------------------------------

const toDocument = (response: ConsentDocumentResponse): ConsentDocument => ({
  documentType: response.document_type,
  version: response.version,
  body: response.body,
  contentSha256: response.content_sha256,
  publishedAt: response.published_at,
});

const toAcceptance = (
  response: ConsentAcceptanceResponse
): ConsentAcceptance => ({
  documentType: response.document_type,
  version: response.version,
  contentSha256: response.content_sha256,
  acceptedAt: response.accepted_at,
});

/**
 * The documents the caller still owes an agreement on, each with the version
 * that is current right now.
 *
 * An empty list is the ordinary answer for anyone who has already been through
 * this - a returning user, or a reload after the POST below has run.
 */
export async function fetchOutstandingConsents(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<ConsentDocument[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot read consent documents.");
  }

  const response = await api.get<ConsentDocumentsResponse>(
    API_ENDPOINTS.consent.documents,
    { token, signal }
  );

  return (response.documents ?? []).map(toDocument);
}

/**
 * Records the caller's agreement to the given documents.
 *
 * Takes the documents the read returned rather than bare types, so the version
 * posted is always the one the backend just said was outstanding.
 */
export async function recordConsent(
  getToken: GetToken,
  documents: readonly ConsentDocument[],
  signal?: AbortSignal
): Promise<ConsentAcceptance[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot record consent.");
  }

  const payload: RecordConsentPayload = {
    acceptances: documents.map(({ documentType, version }) => ({
      document_type: documentType,
      version,
    })),
  };

  const response = await api.post<RecordConsentResponse>(
    API_ENDPOINTS.consent.accept,
    payload,
    { token, signal }
  );

  return (response.acceptances ?? []).map(toAcceptance);
}
